import type {
  DocumentDto,
  DocumentValidationReviewDto,
  DocumentValueDto,
  DocumentValueItemSiteModulesInput,
  ModuleDto,
  MyWorkSiteDto,
} from '@app/contracts';
import { AlertTriangle, Building2, Info, Layers, Plus, Save, Wand2, X } from 'lucide-react';
import type { KeyboardEvent } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useIsoModulesForSiteIds } from '../../../modules/hooks/useModules.js';
import { useMySites } from '../../../progression/hooks/useProgression.js';
import { useCreateDocumentValue, useUpdateDocumentValue } from '../../hooks/useDocuments.js';
import { DocumentRightsButton } from '../DocumentRightsButton.js';
import { DocumentSubmitButton } from '../DocumentSubmitButton.js';
import { InlineDocumentValueValidation } from '../InlineDocumentValueValidation.js';
import {
  type RegistryRowModule,
  type RegistryRowSite,
  mergeMatrixItemPerimeterFromDto,
  normalizeRowModules,
  normalizeRowSites,
} from './RegistryModel.js';

interface SwotItem {
  clientKey: string;
  title: string;
  description: string;
  link: string;
  sites: RegistryRowSite[];
  modules: RegistryRowModule[];
}

type SwotField = 'strengths' | 'weaknesses' | 'opportunities' | 'threats';

interface SwotData {
  strengths: SwotItem[];
  weaknesses: SwotItem[];
  opportunities: SwotItem[];
  threats: SwotItem[];
}

interface Props {
  document: DocumentDto;
  value: DocumentValueDto | undefined;
  /** Cliché soumis : pas d’édition. */
  readOnly?: boolean;
  /** Clés `SerialKey:clientKey` (ex. `Forces:uuid`) pour surbrillance diff. */
  highlightItemKeys?: Set<string>;
  validationReview?: DocumentValidationReviewDto;
}

const FIELD_SERIAL: Record<SwotField, string> = {
  strengths: 'Forces',
  weaknesses: 'Faiblesses',
  opportunities: 'Opportunités',
  threats: 'Menaces',
};

const BADGE_BASE =
  'inline-flex max-w-full shrink-0 items-center gap-0.5 rounded-full border px-2 py-0.5 text-[11px] font-medium leading-none';

function sitesLabel(count: number): string {
  if (count === 0) return 'Aucun site';
  if (count === 1) return '1 site';
  return `${count} sites`;
}

function modulesLabel(count: number): string {
  if (count === 0) return 'Aucun module';
  if (count === 1) return '1 module';
  return `${count} modules`;
}

function parseSwotData(
  serialization: unknown,
  itemSiteModules: DocumentValueDto['itemSiteModules'] | undefined,
): SwotData {
  const byRef = new Map(
    (itemSiteModules ?? []).map(
      (x) => [x.itemRef, { sites: x.sites, modules: x.modules }] as const,
    ),
  );
  if (!serialization || typeof serialization !== 'object') {
    return { strengths: [], weaknesses: [], opportunities: [], threats: [] };
  }
  const s = serialization as Record<string, unknown>;
  const mapQ = (arr: unknown[]) =>
    arr.map((raw) => mergeMatrixItemPerimeterFromDto(normalizeItem(raw), byRef));
  return {
    strengths: mapQ(((s.Forces ?? s.strengths ?? []) as unknown[]) ?? []),
    weaknesses: mapQ(((s.Faiblesses ?? s.weaknesses ?? []) as unknown[]) ?? []),
    opportunities: mapQ(((s.Opportunités ?? s.opportunities ?? []) as unknown[]) ?? []),
    threats: mapQ(((s.Menaces ?? s.threats ?? []) as unknown[]) ?? []),
  };
}

function normalizeItem(raw: unknown): SwotItem {
  const empty = (): SwotItem => ({
    clientKey: crypto.randomUUID(),
    title: '',
    description: '',
    link: '',
    sites: [],
    modules: [],
  });
  if (!raw || typeof raw !== 'object') return empty();
  const r = raw as Record<string, unknown>;
  return {
    clientKey:
      typeof r.clientKey === 'string' && r.clientKey.length > 0 ? r.clientKey : crypto.randomUUID(),
    title: (r.title as string) || '',
    description: (r.description as string) || '',
    link: (r.link as string) || '',
    sites: normalizeRowSites(r.sites),
    modules: normalizeRowModules(r.modules),
  };
}

function itemHasContent(it: SwotItem): boolean {
  return (
    String(it.title || '').trim() !== '' ||
    String(it.description || '').trim() !== '' ||
    String(it.link || '').trim() !== ''
  );
}

function validateSwotForSave(data: SwotData, strictValidation: boolean): string[] {
  const quadrants: { key: SwotField; label: string }[] = [
    { key: 'strengths', label: 'Forces' },
    { key: 'weaknesses', label: 'Faiblesses' },
    { key: 'opportunities', label: 'Opportunités' },
    { key: 'threats', label: 'Menaces' },
  ];
  const err: string[] = [];
  for (const { key, label } of quadrants) {
    data[key].forEach((item, i) => {
      if (!itemHasContent(item)) return;
      if (item.sites.length < 1) {
        err.push(`${label}, élément ${i + 1} : sélectionnez au moins un site concerné.`);
      }
      if (strictValidation && !String(item.title || '').trim()) {
        err.push(`${label}, élément ${i + 1} : le titre est obligatoire.`);
      }
    });
  }
  return err;
}

export function ToolSwot({
  document,
  value,
  readOnly = false,
  highlightItemKeys,
  validationReview,
}: Props) {
  const { data: workSites, isLoading: sitesLoading } = useMySites();
  const workSiteIds = useMemo(() => workSites?.map((s) => s.siteId) ?? [], [workSites]);
  const { modules: moduleChoices, isLoading: modulesLoading } =
    useIsoModulesForSiteIds(workSiteIds);

  const [swotData, setSwotData] = useState<SwotData>(() =>
    parseSwotData(value?.serialization, value?.itemSiteModules),
  );
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [editingItem, setEditingItem] = useState<SwotItem | null>(null);
  const [editingField, setEditingField] = useState<SwotField | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  const createValue = useCreateDocumentValue(document.id);
  const updateValue = useUpdateDocumentValue(document.id);

  useEffect(() => {
    setSwotData(parseSwotData(value?.serialization, value?.itemSiteModules));
    setHasUnsavedChanges(false);
  }, [value?.serialization, value?.itemSiteModules]);

  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedChanges]);

  const handleSave = async () => {
    const validationErrors = validateSwotForSave(swotData, document.strictValidation);
    if (validationErrors.length > 0) {
      alert(validationErrors.join('\n'));
      return;
    }

    setSaveStatus('saving');
    const strip = (items: SwotItem[]) =>
      items.map(({ clientKey, title, description, link }) => ({
        clientKey,
        title,
        description,
        link,
      }));
    const serialization = {
      Forces: strip(swotData.strengths),
      Faiblesses: strip(swotData.weaknesses),
      Opportunités: strip(swotData.opportunities),
      Menaces: strip(swotData.threats),
    };
    const collectItemSiteModules = (data: SwotData): DocumentValueItemSiteModulesInput[] => {
      const acc: DocumentValueItemSiteModulesInput[] = [];
      const pushAll = (items: SwotItem[]) => {
        for (const it of items) {
          acc.push({
            itemRef: it.clientKey,
            siteIds: it.sites.map((s) => s.id),
            moduleIds: it.modules.map((m) => m.id),
          });
        }
      };
      pushAll(data.strengths);
      pushAll(data.weaknesses);
      pushAll(data.opportunities);
      pushAll(data.threats);
      return acc;
    };
    const itemSiteModules = collectItemSiteModules(swotData);
    try {
      if (value?.id) {
        await updateValue.mutateAsync({
          valueId: value.id,
          input: { serialization, itemSiteModules },
        });
      } else {
        await createValue.mutateAsync({ serialization, itemSiteModules });
      }
      setHasUnsavedChanges(false);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  };

  const addItem = (field: SwotField) => {
    const newItem: SwotItem = {
      clientKey: crypto.randomUUID(),
      title: '',
      description: '',
      link: '',
      sites: [],
      modules: [],
    };
    setSwotData((prev) => ({ ...prev, [field]: [...prev[field], newItem] }));
    setEditingItem(newItem);
    setEditingField(field);
    setEditingIndex(swotData[field].length);
    setShowModal(true);
    setHasUnsavedChanges(true);
  };

  const removeItem = (field: SwotField, index: number) => {
    setSwotData((prev) => ({ ...prev, [field]: prev[field].filter((_, i) => i !== index) }));
    setHasUnsavedChanges(true);
  };

  const openModal = (item: SwotItem, field: SwotField, index: number) => {
    setEditingItem({
      ...item,
      sites: item.sites.map((s) => ({ ...s })),
      modules: item.modules.map((m) => ({ ...m })),
    });
    setEditingField(field);
    setEditingIndex(index);
    setShowModal(true);
  };

  const toggleEditingSite = (site: MyWorkSiteDto, selected: boolean) => {
    setEditingItem((prev) => {
      if (!prev) return prev;
      const nextSites = selected
        ? [
            ...prev.sites.filter((s) => s.id !== site.siteId),
            {
              id: site.siteId,
              name: site.siteName,
              city: site.city ?? undefined,
              country: site.country ?? undefined,
            },
          ]
        : prev.sites.filter((s) => s.id !== site.siteId);
      return { ...prev, sites: nextSites };
    });
  };

  const toggleEditingModule = (mod: ModuleDto, selected: boolean) => {
    setEditingItem((prev) => {
      if (!prev) return prev;
      const nextModules = selected
        ? [
            ...prev.modules.filter((m) => m.id !== mod.id),
            { id: mod.id, name: mod.name, code: mod.code },
          ]
        : prev.modules.filter((m) => m.id !== mod.id);
      return { ...prev, modules: nextModules };
    });
  };

  const saveEditingItem = () => {
    if (editingField !== null && editingIndex !== null && editingItem) {
      setSwotData((prev) => ({
        ...prev,
        [editingField]: prev[editingField].map((it, i) =>
          i === editingIndex ? { ...editingItem } : it,
        ),
      }));
      setHasUnsavedChanges(true);
    }
    setShowModal(false);
    setEditingItem(null);
    setEditingField(null);
    setEditingIndex(null);
  };

  const DEMO_DATA = {
    strengths: [
      {
        title: 'Engagement fort de la direction',
        description: 'La direction est impliquée et soutient activement la démarche qualité.',
        link: '',
      },
      {
        title: 'Processus clés documentés',
        description: 'Les procédures principales sont formalisées et accessibles aux équipes.',
        link: '',
      },
      {
        title: 'Équipe expérimentée',
        description: "Les collaborateurs disposent d'une expertise solide dans leur domaine.",
        link: '',
      },
    ],
    weaknesses: [
      {
        title: 'Ressources dédiées limitées',
        description:
          'Le temps alloué aux activités qualité reste insuffisant par rapport aux objectifs.',
        link: '',
      },
      {
        title: 'Système de veille réglementaire incomplet',
        description: "La surveillance des évolutions légales et normatives n'est pas systématisée.",
        link: '',
      },
    ],
    opportunities: [
      {
        title: 'Accès à de nouveaux marchés',
        description:
          "La certification ouvre des opportunités commerciales avec des donneurs d'ordre exigeants.",
        link: '',
      },
      {
        title: 'Digitalisation des processus',
        description:
          'Des outils numériques permettent de gagner en efficacité sur la gestion documentaire.',
        link: '',
      },
    ],
    threats: [
      {
        title: 'Concurrence certifiée croissante',
        description: 'De plus en plus de concurrents obtiennent des certifications similaires.',
        link: '',
      },
      {
        title: 'Évolutions réglementaires fréquentes',
        description:
          'Les mises à jour normatives nécessitent une adaptation continue des pratiques.',
        link: '',
      },
    ],
  };

  const fillWithDemoData = () => {
    const siteRefs =
      workSites?.map((s) => ({
        id: s.siteId,
        name: s.siteName,
        city: s.city ?? undefined,
        country: s.country ?? undefined,
      })) ?? [];
    const moduleRefs = moduleChoices.map((m) => ({ id: m.id, name: m.name, code: m.code }));
    if (siteRefs.length === 0) {
      alert(
        'Aucun site n’est assigné à votre compte : les éléments démo n’auront pas de site. Affectez-vous à au moins un site, ou cochez les sites après coup.',
      );
    }
    if (moduleRefs.length === 0 && workSiteIds.length > 0 && !modulesLoading) {
      alert(
        'Aucun module ISO sur vos sites : les cases modules restent vides. Assignez des modules dans la page Modules.',
      );
    }
    const withKeys = (
      items: Array<{ title: string; description: string; link: string }>,
    ): SwotItem[] =>
      items.map((i) => ({
        clientKey: crypto.randomUUID(),
        ...i,
        sites: siteRefs.map((s) => ({ ...s })),
        modules: moduleRefs.map((m) => ({ ...m })),
      }));
    setSwotData({
      strengths: withKeys(DEMO_DATA.strengths),
      weaknesses: withKeys(DEMO_DATA.weaknesses),
      opportunities: withKeys(DEMO_DATA.opportunities),
      threats: withKeys(DEMO_DATA.threats),
    });
    setHasUnsavedChanges(true);
  };

  const demoButtonDisabled = sitesLoading || (workSiteIds.length > 0 && modulesLoading);

  const valueValidationRow =
    validationReview?.canValidate && value?.id
      ? validationReview.validations.find((v) => v.documentValueId === value.id)
      : undefined;

  const handleTitleChange = (field: SwotField, index: number, title: string) => {
    setSwotData((prev) => ({
      ...prev,
      [field]: prev[field].map((it, i) => (i === index ? { ...it, title } : it)),
    }));
    setHasUnsavedChanges(true);
  };

  const renderSection = (field: SwotField, title: string) => (
    <div
      className="flex flex-col p-5"
      onMouseEnter={(e) => {
        const btn = e.currentTarget.querySelector<HTMLButtonElement>('.add-btn');
        if (btn) btn.style.opacity = '1';
      }}
      onMouseLeave={(e) => {
        const btn = e.currentTarget.querySelector<HTMLButtonElement>('.add-btn');
        if (btn) btn.style.opacity = '0';
      }}
    >
      <h3 className="mb-3 text-center text-sm font-bold uppercase tracking-wide text-slate-700 border-b border-slate-200 pb-2">
        {title}
      </h3>
      <div className="flex flex-col gap-2 flex-1">
        {swotData[field].map((item, index) => {
          const hl = highlightItemKeys?.has(`${FIELD_SERIAL[field]}:${item.clientKey}`) ?? false;
          return (
            <div
              key={item.clientKey}
              className={`flex flex-col gap-1 border-b border-slate-100 pb-2 ${
                hl ? 'rounded-md bg-amber-50/90 ring-2 ring-amber-300/80 px-1 -mx-1' : ''
              }`}
            >
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-400 w-4 shrink-0">{index + 1}.</span>
                <input
                  type="text"
                  readOnly={readOnly}
                  className="flex-1 border-none outline-none text-sm bg-transparent text-slate-700 placeholder:text-slate-300"
                  placeholder="Label de l'élément"
                  value={item.title}
                  onChange={(e) => handleTitleChange(field, index, e.target.value)}
                />
                {!readOnly ? (
                  <>
                    <button
                      type="button"
                      className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-400 text-white hover:bg-slate-600"
                      onClick={() => openModal(item, field, index)}
                      title="Modifier les détails (sites, modules…)"
                    >
                      <Info className="h-3 w-3" />
                    </button>
                    <button
                      type="button"
                      className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-slate-300 text-slate-600 hover:bg-red-500 hover:text-white"
                      onClick={() => removeItem(field, index)}
                      title="Supprimer"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </>
                ) : null}
              </div>
              <div className="flex flex-wrap items-center gap-1.5 pl-6">
                <span
                  className={`${BADGE_BASE} border-slate-200 bg-white text-slate-700`}
                  title="Sites associés"
                >
                  <Building2 className="h-3 w-3 shrink-0 opacity-70" aria-hidden />
                  {sitesLabel(item.sites.length)}
                </span>
                <span
                  className={`${BADGE_BASE} border-slate-200 bg-white text-slate-700`}
                  title="Modules ISO associés"
                >
                  <Layers className="h-3 w-3 shrink-0 opacity-70" aria-hidden />
                  {modulesLabel(item.modules.length)}
                </span>
              </div>
            </div>
          );
        })}
      </div>
      {!readOnly ? (
        <button
          type="button"
          className="add-btn mt-3 mx-auto flex h-7 w-7 items-center justify-center rounded-full bg-slate-300 text-slate-600 hover:bg-emerald-500 hover:text-white transition-all"
          style={{ opacity: 0 }}
          onClick={() => addItem(field)}
          title="Ajouter"
        >
          <Plus className="h-4 w-4" />
        </button>
      ) : null}
    </div>
  );

  return (
    <div className="relative">
      {/* Header */}
      <div className="mb-4 flex items-start justify-between rounded-xl border border-slate-200 bg-slate-50 p-4">
        <div>
          <h1 className="text-lg font-bold text-slate-900">{document.name}</h1>
          <p className="mt-0.5 text-xs text-slate-500">
            Créé le {new Date(document.createdAt).toLocaleDateString('fr-FR')} · Modifié le{' '}
            {new Date(document.updatedAt).toLocaleDateString('fr-FR')} · Sites et modules par
            élément (via l’icône info)
          </p>
          {hasUnsavedChanges && (
            <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
              <AlertTriangle className="h-3 w-3" />
              Modifications non sauvegardées
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!readOnly ? <DocumentSubmitButton document={document} /> : null}
          <DocumentRightsButton document={document} />
          {!readOnly ? (
            <>
              <button
                type="button"
                onClick={fillWithDemoData}
                disabled={demoButtonDisabled}
                title={
                  demoButtonDisabled
                    ? 'Chargement des sites ou des modules pour préremplir les cases…'
                    : undefined
                }
                className="flex items-center gap-2 rounded-lg border border-violet-200 bg-violet-50 px-4 py-2 text-sm font-medium text-violet-700 transition-colors hover:bg-violet-100 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Wand2 className="h-4 w-4" />
                Démo
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saveStatus === 'saving'}
                className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${saveStatus === 'saved' ? 'bg-emerald-500 text-white' : saveStatus === 'error' ? 'bg-red-500 text-white' : hasUnsavedChanges ? 'bg-amber-500 text-white hover:bg-amber-600' : 'bg-slate-200 text-slate-600 hover:bg-slate-300'}`}
              >
                <Save className="h-4 w-4" />
                {saveStatus === 'saving'
                  ? 'Sauvegarde…'
                  : saveStatus === 'saved'
                    ? 'Sauvegardé !'
                    : saveStatus === 'error'
                      ? 'Erreur'
                      : 'Sauvegarder'}
              </button>
            </>
          ) : null}
        </div>
      </div>

      {valueValidationRow && validationReview ? (
        <div className="mb-4">
          <InlineDocumentValueValidation
            documentId={document.id}
            canValidate={validationReview.canValidate}
            validationRow={valueValidationRow}
          />
        </div>
      ) : null}

      {/* 2×2 matrix */}
      <div
        className="grid border border-slate-300 rounded-xl overflow-hidden"
        style={{ gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr', minHeight: 500 }}
      >
        <div className="border-r border-b border-slate-300">
          {renderSection('strengths', 'Forces')}
        </div>
        <div className="border-b border-slate-300">{renderSection('weaknesses', 'Faiblesses')}</div>
        <div className="border-r border-slate-300">
          {renderSection('opportunities', 'Opportunités')}
        </div>
        <div>{renderSection('threats', 'Menaces')}</div>
      </div>

      {/* Edit modal */}
      {showModal && editingItem && !readOnly ? (
        <>
          {/* biome-ignore lint/a11y/useKeyWithClickEvents: overlay modal */}
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
            onClick={() => setShowModal(false)}
          >
            <div
              className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-xl bg-white p-6 shadow-xl"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e: KeyboardEvent) => e.stopPropagation()}
            >
              <div className="mb-4 flex items-center justify-between">
                <h3 className="font-semibold text-slate-900">Éditer l'élément</h3>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="text-slate-400 hover:text-slate-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="space-y-4">
                <label className="flex flex-col gap-1 text-sm font-medium text-slate-700">
                  Titre *
                  <input
                    type="text"
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-normal outline-none focus:border-emerald-500"
                    value={editingItem.title}
                    onChange={(e) =>
                      setEditingItem((prev) => (prev ? { ...prev, title: e.target.value } : prev))
                    }
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm font-medium text-slate-700">
                  Description
                  <textarea
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-normal outline-none focus:border-emerald-500 min-h-[80px] resize-y"
                    value={editingItem.description}
                    onChange={(e) =>
                      setEditingItem((prev) =>
                        prev ? { ...prev, description: e.target.value } : prev,
                      )
                    }
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm font-medium text-slate-700">
                  Lien
                  <input
                    type="url"
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-normal outline-none focus:border-emerald-500"
                    value={editingItem.link}
                    onChange={(e) =>
                      setEditingItem((prev) => (prev ? { ...prev, link: e.target.value } : prev))
                    }
                    placeholder="https://"
                  />
                </label>

                <div className="space-y-2 rounded-lg border border-slate-100 bg-slate-50/80 p-3">
                  <div>
                    <p className="text-xs font-medium text-slate-700">Sites concernés</p>
                    <p className="mt-0.5 text-[11px] text-slate-500">
                      Obligatoire pour chaque élément renseigné avant « Sauvegarder ».
                      {document.strictValidation
                        ? ' Validation stricte : le titre est aussi requis.'
                        : ''}
                    </p>
                  </div>
                  {sitesLoading ? (
                    <p className="text-xs text-slate-500">Chargement des sites…</p>
                  ) : !workSites?.length ? (
                    <p className="text-xs text-amber-700">
                      Aucun site assigné à votre compte. Demandez à un administrateur de vous
                      affecter à un site.
                    </p>
                  ) : (
                    <div className="max-h-36 space-y-1.5 overflow-y-auto rounded-md border border-slate-200 bg-white p-2">
                      {workSites.map((site) => {
                        const checked = editingItem.sites.some((s) => s.id === site.siteId);
                        return (
                          <label
                            key={site.siteId}
                            className="flex cursor-pointer items-start gap-2 rounded px-1 py-0.5 text-sm text-slate-700 hover:bg-slate-50"
                          >
                            <input
                              type="checkbox"
                              className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 text-emerald-600"
                              checked={checked}
                              onChange={(e) => toggleEditingSite(site, e.target.checked)}
                            />
                            <span className="font-normal">
                              {site.siteName}
                              {site.city ? (
                                <span className="text-slate-500"> · {site.city}</span>
                              ) : null}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="space-y-2 rounded-lg border border-slate-100 bg-slate-50/80 p-3">
                  <div>
                    <p className="text-xs font-medium text-slate-700">Modules ISO concernés</p>
                    <p className="mt-0.5 text-[11px] text-slate-500">
                      Optionnel — modules ISO déjà assignés à vos sites.
                    </p>
                  </div>
                  {!workSites?.length ? (
                    <p className="text-xs text-slate-500">
                      Ajoutez d’abord des sites à votre compte pour lister les modules concernés.
                    </p>
                  ) : modulesLoading ? (
                    <p className="text-xs text-slate-500">Chargement des modules…</p>
                  ) : !moduleChoices.length ? (
                    <p className="text-xs text-slate-500">
                      Aucun module ISO assigné à vos sites. Configurez-les dans la page Modules.
                    </p>
                  ) : (
                    <div className="max-h-40 space-y-1.5 overflow-y-auto rounded-md border border-slate-200 bg-white p-2">
                      {moduleChoices.map((mod) => {
                        const checked = editingItem.modules.some((m) => m.id === mod.id);
                        return (
                          <label
                            key={mod.id}
                            className="flex cursor-pointer items-start gap-2 rounded px-1 py-0.5 text-sm text-slate-700 hover:bg-slate-50"
                          >
                            <input
                              type="checkbox"
                              className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 text-emerald-600"
                              checked={checked}
                              onChange={(e) => toggleEditingModule(mod, e.target.checked)}
                            />
                            <span className="font-normal">
                              <span className="font-medium text-slate-800">{mod.code}</span>
                              <span className="text-slate-600"> — {mod.name}</span>
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-5 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={saveEditingItem}
                  className="rounded-lg bg-emerald-500 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-600"
                >
                  Confirmer
                </button>
              </div>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
